import { useNav } from './ui';
import type { ReactNode } from 'react';
import { isSystemLocked } from '@noorview/core';
import { attempt, backend, useApp } from '../data/store';
import { choose } from '../ui/controls';
import { Icon } from '../ui/icons';

/**
 * The system lock (the dashboard's Lock button): while every screen is locked, nothing can be changed. Screens keep
 * running. The database refuses changes too, so this can't be got round from another browser.
 */
export const useLocked = () => useApp((s) => !!s.ws && isSystemLocked(s.ws.screens));
export const lockedNow = () => { const ws = useApp.getState().ws; return !!ws && isSystemLocked(ws.screens); };

export async function setSystemLock(on: boolean) {
  const ws = useApp.getState().ws;
  if (!ws?.screens.length) return false;
  const ok = await choose(on ? 'Lock NoorView?' : 'Unlock NoorView?', {
    body: on
      ? 'Nothing can be changed until you unlock: the Studio, settings, timetable and screens. Your screens keep showing what they show now.'
      : 'Everyone on your team will be able to make changes again.',
    choices: [{ value: 'no', label: 'Cancel', kind: 'plain' }, { value: 'yes', label: on ? 'Lock' : 'Unlock', kind: 'primary' }],
  });
  if (ok !== 'yes') return false;
  return attempt(() => backend.updateScreens(ws.screens.map((s) => s.id), { locked: on }), on ? 'NoorView locked. Nothing can be changed until you unlock.' : 'NoorView unlocked');
}

/** Runs `fn` if unlocked; otherwise explains and offers to unlock. */
export async function unlockedOr(fn: () => void) {
  if (!lockedNow()) { fn(); return; }
  const pick = await choose('NoorView is locked', {
    body: 'Nothing can be changed while the screens are locked. Unlock them to make changes.',
    choices: [{ value: 'no', label: 'Keep locked', kind: 'plain' }, { value: 'unlock', label: 'Unlock', kind: 'primary' }],
  });
  if (pick === 'unlock' && (await attempt(() => backend.updateScreens(useApp.getState().ws!.screens.map((s) => s.id), { locked: false }), 'NoorView unlocked'))) fn();
}

/** Shows `children` greyed out and untouchable while locked, with a short note above. */
export function LockedArea({ children, what = 'these settings' }: { children: ReactNode; what?: string }) {
  const locked = useLocked();
  return (
    <>
      {locked && <div className="banner lock-banner" role="status"><Icon name="lock" size={18} /><span style={{ flex: 1 }}>NoorView is locked, so {what} can’t be changed.</span>
        <button type="button" className="btn sm primary" onClick={() => void setSystemLock(false)}>Unlock</button></div>}
      <fieldset className="lock-fs" disabled={locked} aria-disabled={locked}>{children}</fieldset>
    </>
  );
}

/** Covers the Studio while locked: all of NoorView, or the screen (or screens) this design is on. */
export function StudioLock({ screens }: { screens?: ReadonlyArray<{ id: string; name: string }> }) {
  const nav = useNav();
  const names = screens?.map((s) => s.name).sort();
  const one = names?.length === 1;
  const unlock = () => screens?.length
    ? void attempt(() => backend.updateScreens(screens.map((s) => s.id), { locked: false }), `${names!.join(', ')} unlocked`)
    : void setSystemLock(false);
  return (
    <div className="studio-lock" role="dialog" aria-modal="true" aria-labelledby="studio-lock-t">
      <div className="studio-lock-card glass">
        <span className="studio-lock-ic"><Icon name="lock" size={30} /></span>
        <h2 id="studio-lock-t">{names?.length ? `${one ? names[0] : 'These screens'} ${one ? 'is' : 'are'} locked` : 'The Studio is locked'}</h2>
        <p>{names?.length
          ? `${one ? 'This screen is' : `${names.join(', ')} are`} locked, so ${one ? 'its' : 'their'} design can’t be changed. Unlock ${one ? 'it' : 'them'} when you’re ready to edit.`
          : 'All screens are locked, so designs can’t be changed. Unlock them on the dashboard when you’re ready to edit.'}</p>
        <div className="row" style={{ justifyContent: 'center', gap: 12 }}>
          <button type="button" className="btn plain" onClick={() => nav('/')}>Back to dashboard</button>
          <button type="button" className="btn primary" onClick={unlock}><Icon name="unlock" size={18} />Unlock</button>
        </div>
      </div>
    </div>
  );
}
